import type { TooltipItem } from 'chart.js';
import 'chart.js/auto';
import { Doughnut } from 'react-chartjs-2';

interface UsageDoughnutChartProps {
  dataProcHours: number;
  usedDataProcHours: number;
  usedDataProcHoursPercentage: number;
  availableDataProcHours: number;
  availableDataProcHoursPercentage: number;
  threshold: number;
  thresholdPercentage: number;
  thresholdValueHours: number;
  thresholdRemainingHours: number;
}

/**
 * Doughnut chart showing DPH usage vs available hours
 * Color coding:
 * - Green: Used hours under threshold
 * - Red: Used hours over threshold
 * - Bright red: Over limit (negative available)
 * - Gray: Available hours
 */
export const UsageDoughnutChart = ({
  dataProcHours,
  usedDataProcHours,
  usedDataProcHoursPercentage,
  availableDataProcHours: _availableDataProcHours,
  availableDataProcHoursPercentage,
  thresholdPercentage,
  thresholdValueHours,
  thresholdRemainingHours: _thresholdRemainingHours,
}: UsageDoughnutChartProps) => {
  // Ensure percentages don't go negative for display
  let displayAvailablePercentage = availableDataProcHoursPercentage;
  if (displayAvailablePercentage < 0) {
    displayAvailablePercentage = 0;
  }

  // Determine colors based on threshold
  const exceedsThreshold = usedDataProcHours > thresholdValueHours;
  let usedColor = exceedsThreshold ? 'rgba(255, 99, 132, 0.7)' : 'rgba(3, 193, 165, 0.7)';

  // If over limit entirely, use bright red
  if (availableDataProcHoursPercentage <= 0) {
    usedColor = 'rgba(255, 0, 0, 1)';
  }

  const availableColor = 'rgba(204, 204, 204, 0.7)';

  const data = {
    labels: ['Used Hours', 'Available Hours'],
    datasets: [
      {
        label: 'Hours Usage',
        data: [usedDataProcHoursPercentage, displayAvailablePercentage],
        backgroundColor: [usedColor, availableColor],
        borderWidth: 0,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    animation: false as const,
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
        align: 'start' as const,
        labels: {
          generateLabels: () => {
            // Custom legend showing all possible states
            return [
              {
                text: 'Used Hours - Under Threshold',
                fillStyle: 'rgba(3, 193, 165, 0.7)',
                hidden: false,
                index: 0,
              },
              {
                text: 'Used Hours - Over Threshold',
                fillStyle: 'rgba(255, 99, 132, 0.7)',
                hidden: false,
                index: 1,
              },
              {
                text: 'Available Hours - Remaining',
                fillStyle: 'rgba(204, 204, 204, 0.7)',
                hidden: false,
                index: 2,
              },
              {
                text: 'Available Hours - Over',
                fillStyle: 'rgba(255, 0, 0, 1)',
                hidden: false,
                index: 3,
              },
            ];
          },
        },
        onClick: () => {
          // Disable legend click
        },
      },
      tooltip: {
        callbacks: {
          label(tooltipItem: TooltipItem<'doughnut'>) {
            const label = tooltipItem.label || '';
            const value = typeof tooltipItem.raw === 'number' ? tooltipItem.raw.toFixed(2) : '0.00';
            const hours =
              typeof tooltipItem.raw === 'number'
                ? ((tooltipItem.raw * dataProcHours) / 100).toFixed(2)
                : '0.00';

            if (label === 'Available Hours') {
              return `${value}% (${hours} Hours Remaining)`;
            }
            return `${value}% (${hours} Hours Used) [Threshold: ${thresholdPercentage}%]`;
          },
        },
      },
    },
  };

  return <Doughnut data={data} options={options} />;
};
